import { MinusIcon } from '@va/icons';
import {
  Button,
  Paragraph,
  ParagraphProps,
  RadioInputSectionList,
  fontWeights,
  paragraphSizes,
} from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import { useMergeRefs } from '@va/util/hooks';
import classNames from 'classnames';
import React, { forwardRef, useRef, useState } from 'react';

export type SelectDropdownInputOption = {
  icon?: React.ReactNode;
  label: string;
  labelTooltip?: string;
  smallLabel?: string;
  id?: string | number;
  value: string;
  disabled?: boolean;
  ref?: React.RefObject<HTMLDivElement | null>;
  infoTooltip?: string;
  info?: string;
  infoProps?: Omit<ParagraphProps, 'children'>;
  endIcon?: React.ReactNode;
  'data-testid'?: string;
  selectedColor?: string;
  [key: string]: unknown;
};

export type SearchInputProps = {
  customOnSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchValue?: string;
  customClearField?: () => void;
  isVisible?: boolean;
  placeholder?: string;
  'data-testid'?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
};

type SelectDropdownInputProps = {
  options: SelectDropdownInputOption[];
  selectedOption?: SelectDropdownInputOption;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onChange: (option: SelectDropdownInputOption) => void;
  clearSelectedOption: () => void;
  className?: string;
  isLoading?: boolean;
  searchInputProps?: SearchInputProps;
  disabled?: boolean;
  size?: 'small' | 'large';
  'data-testid'?: string;
  noSelectionErrorMessage?: string;
};

export const SelectDropdownInput = forwardRef<HTMLButtonElement, SelectDropdownInputProps>(
  (
    {
      options,
      selectedOption,
      onChange,
      icon,
      title,
      subtitle,
      clearSelectedOption,
      className,
      searchInputProps,
      isLoading,
      disabled,
      size = 'large',
      'data-testid': dataTestId,
      noSelectionErrorMessage,
    },
    ref,
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const mergedRef = useMergeRefs([buttonRef, ref]);

    const triggerClassName = classNames(
      'h-72 shadow-overlay w-full px-18px flex gap-15px items-center rounded-12 text-start',
      {
        'hover:bg-white-snow active:bg-gray-concrete group cursor-pointer': !selectedOption && !disabled,
        'bg-white/60 cursor-not-allowed': disabled,
        'bg-white': !disabled,
        '!h-12 !p-1.5 !pr-18px rounded-15': size === 'small',
      },
      className,
    );

    const triggerContent = (
      <>
        {icon && <div className='shrink-0'>{icon}</div>}
        <div className='flex-1 overflow-hidden'>
          <Paragraph
            className={classNames('truncate', {
              '!leading-18': size === 'small',
            })}
            weight={fontWeights.medium}
          >
            {selectedOption ? selectedOption.label : title}
          </Paragraph>
          <Paragraph
            className={classNames('truncate', {
              '!leading-15': size === 'small',
            })}
            size={paragraphSizes.tiny}
            colorClassName='text-gray-devil'
          >
            {selectedOption ? selectedOption.value : subtitle}
          </Paragraph>
        </div>
      </>
    );

    // With an option selected the dropdown can't open and the only action is clearing it, so the trigger isn't a
    // button then: the clear button would otherwise be nested inside another button, which is invalid HTML
    if (selectedOption) {
      return (
        <div className='relative'>
          <div className={triggerClassName} data-testid={dataTestId}>
            {triggerContent}
            <Button
              disabled={disabled}
              onClick={clearSelectedOption}
              className='ml-auto'
              color='tertiary'
              icon={() => <MinusIcon />}
            />
          </div>
        </div>
      );
    }

    return (
      <div className='relative'>
        <TooltipWrapper
          disabled={disabled}
          arrow={false}
          open={isDropdownVisible}
          onOpenChange={(open) => {
            if (!open) buttonRef.current?.focus();
            setIsDropdownVisible(open);
          }}
          tooltipClassNames='shadow-overlay rounded-12'
          useDefaultStyle={false}
          placement='bottom'
          interactive={true}
          trigger='click'
          content={
            <RadioInputSectionList
              id={`${title}-list`}
              searchInputProps={searchInputProps}
              isLoading={isLoading}
              options={options}
              selectedOption={selectedOption}
              onChange={(option) => {
                setIsDropdownVisible(false);
                onChange(option);
              }}
              noSelectionErrorMessage={noSelectionErrorMessage}
            />
          }
          useParentWidth
        >
          <button ref={mergedRef} className={triggerClassName} data-testid={dataTestId}>
            {triggerContent}
            <DropdownArrow open={isDropdownVisible} className='ml-auto' color='#696969' />
          </button>
        </TooltipWrapper>
      </div>
    );
  },
);
